/**
 * The campaign export, rendered as something a person would keep.
 *
 * The endpoint answers JSON — campaign, members, canon, transcript — and the
 * plan asks for "a readable artifact (the group's story — the main thing
 * anyone would want to keep)". A `.json` download is not that: it is the same
 * facts in the one shape nobody reads for pleasure.
 *
 * **Formatting only.** Nothing here decides what may be exported; the endpoint
 * is Owner-only and already filtered, and the transcript it returns is the
 * unfiltered one *on purpose* — the export is the group's story rather than
 * one player's view of it. This turns that into Markdown and nothing more.
 */

/**
 * The endpoint's payload, as it actually is.
 *
 * `members` is a **join**, not a flat list — `{ character, membership }` — and
 * a canon entry keeps its content in an untyped `data` blob rather than a
 * `description` column. Both were guessed wrong first time round, which is
 * what reading the route rather than assuming it is for.
 */
export interface CampaignExport {
  readonly exportedAt: string;
  readonly campaign: { readonly name: string; readonly premise?: string };
  readonly members?: readonly {
    readonly character: { readonly name: string };
  }[];
  readonly canon?: readonly {
    readonly name: string;
    readonly type: string;
    readonly data?: Record<string, unknown>;
  }[];
  readonly transcript?: readonly {
    readonly authorName?: string | null;
    readonly role?: string;
    readonly content: string;
  }[];
}

/**
 * Markdown, because it reads as plain text and opens as a document.
 *
 * The transcript keeps its **speaker labels and its order** and is not
 * summarised: an export that abridged the story would be answering a different
 * question from the one anybody asks of it.
 */
export function toMarkdown(data: CampaignExport): string {
  const lines: string[] = [];

  lines.push(`# ${data.campaign.name}`, '');
  if (data.campaign.premise) lines.push(data.campaign.premise, '');
  lines.push(`*Exported ${new Date(data.exportedAt).toLocaleString()}*`, '');

  const members = data.members ?? [];
  if (members.length > 0) {
    lines.push('## The table', '');
    for (const member of members) {
      lines.push(`- ${member.character.name}`);
    }
    lines.push('');
  }

  const canon = data.canon ?? [];
  if (canon.length > 0) {
    lines.push('## The world', '');
    for (const entry of canon) {
      lines.push(`### ${entry.name}`, '', `*${entry.type}*`, '');
      // The content lives in an untyped `data` blob whose keys differ by canon
      // type, so the string fields are written out by name rather than one
      // being picked as "the" description — which would silently drop whatever
      // a place, a faction and a person each carry that the others do not.
      //
      // **References are skipped.** A `regionId` is machinery, and printing
      // "regionId: lowlands" in the middle of the story is the same mistake as
      // labelling an engine line with its author. The `*Id` convention is the
      // schema's own, so this reads a naming rule rather than guessing which
      // keys are prose.
      for (const [key, value] of Object.entries(entry.data ?? {})) {
        if (/id$/i.test(key)) continue;
        if (typeof value === 'string' && value.trim() !== '') {
          lines.push(`**${key}:** ${value}`, '');
        }
      }
    }
  }

  const transcript = data.transcript ?? [];
  if (transcript.length > 0) {
    lines.push('## What happened', '');
    for (const message of transcript) {
      // A system or engine line has no speaker worth naming, and prefixing one
      // with "engine:" would put the machinery in the middle of the story.
      const speaker =
        message.role === 'system' || message.role === 'engine'
          ? undefined
          : (message.authorName ?? undefined);
      lines.push(speaker ? `**${speaker}:** ${message.content}` : `*${message.content}*`, '');
    }
  }

  return lines.join('\n');
}

/** A filename that sorts and survives a filesystem. */
export function exportFilename(name: string, exportedAt: string): string {
  const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const day = exportedAt.slice(0, 10);
  return `${slug || 'campaign'}-${day}.md`;
}
